/**
 * 实时数据管理 Composable
 * 管理实时数据的获取、更新、缓存等功能
 */

import realtimeAPI from '@/api/realtime';
import logger from '@/utils/logger'

export default function useRealtimeData() {
  return {
    methods: {
      /**
       * 获取实时状态数据
       */
      async fetchRealtimeData() {
        if (this.isSliding) {
          return; // 滑动期间暂停实时数据查询，避免触发重新渲染
        }
        try {
          // 如果没有预约数据，跳过查询
          if (!this.appointmentData || this.appointmentData.length === 0) {
            return;
          }

          // 检查是否存在真实的预约治疗数据（有roomId的数据）
          const realAppointments = this.appointmentData.filter(appointment => 
            appointment && appointment.roomId
          );
          
          // 如果没有真实预约数据，直接使用虚拟数据，跳过API调用
          if (realAppointments.length === 0) {
            this.updateVirtualBrainwaveData();
            return;
          }

          // 从真实预约数据中提取治疗室ID列表
          const roomIds = realAppointments.map(appointment => appointment.roomId);
          
          // 统一使用后端接口获取实时数据
          const response = await realtimeAPI.getRealtimeStatus(roomIds);
          
          // 处理API响应
          this.handleRealtimeDataResponse(response);
          
        } catch (error) {
          // 捕获未预期的异常
          this.handleRealtimeDataError(error);
        }
      },

      /**
       * 处理实时数据API响应
       */
      handleRealtimeDataResponse(response) {
        try {
          // 检查响应是否成功
          if (response && response.success !== false && response.status === 'success' && response.data) {
            // 验证数据格式
            if (typeof response.data === 'object' && response.data !== null) {
              // 更新实时数据
              this.updateRealtimeData(response.data);
            } else {
              this.addSystemLog('实时数据格式异常，已切换到虚拟数据模式');
              this.updateVirtualBrainwaveData();
            }
          } else if (response && response.success === false) {
            this.addSystemLog(`实时数据服务暂时不可用: ${response.message || '未知原因'}`);
            this.updateVirtualBrainwaveData();
          } else {
            this.addSystemLog('实时数据API响应格式异常，已切换到虚拟数据模式');
            this.updateVirtualBrainwaveData();
          }
        } catch (error) {
          this.addSystemLog('处理实时数据响应异常: ' + (error.message || '未知错误'));
          this.updateVirtualBrainwaveData();
        }
      },

      /**
       * 处理实时数据获取过程中的异常
       */
      handleRealtimeDataError(error) {
        // 记录友好的系统日志
        this.addSystemLog('实时数据获取异常，已自动切换到虚拟数据模式');
        
        // 使用虚拟数据保持系统正常运行
        this.updateVirtualBrainwaveData();
      },

      /**
       * 验证脑波数据中的房间ID是否与预约数据匹配
       */
      validateRoomData(brainwaveData, appointment) {
        if (!brainwaveData || !appointment) return false;
        
        // 只使用治疗室ID进行验证
        const expectedRoomId = appointment.roomId;

        // 后端返回的房间ID与预约数据中的治疗室ID直接对比
        if (brainwaveData.room_id && brainwaveData.room_id.toString() !== expectedRoomId.toString()) {
          logger.warn(`治疗室ID不匹配: 期望 ${expectedRoomId}, 实际 ${brainwaveData.room_id}`);
          return false;
        }
        
        return true;
      },

      /**
       * 使用缓存的脑波数据
       */
      useCachedBrainwaveData(appointment) {
        // 检查治疗状态：除非治疗已结束，否则不将值设为0
        const treatmentStatus = this.getTreatmentStatus(appointment);
        const isTreatmentEnded = treatmentStatus === '已结束';
        
        if (appointment.lastValidBrainwaveData) {
          const cachedData = appointment.lastValidBrainwaveData;
          
          // 防御性检查：确保缓存数据有效
          const hasValidCachedValue = (cachedData.delta > 0 || cachedData.theta > 0 || 
                                       cachedData.alpha > 0 || cachedData.beta > 0 || cachedData.gamma > 0);
          
          if (hasValidCachedValue || isTreatmentEnded) {
            // 防御性检查：在赋值前验证每个值
            const safeCachedValue = (cachedValue, currentValue, bandName) => {
              // 如果缓存值为0且治疗未结束，优先使用当前值
              if (cachedValue === 0 && !isTreatmentEnded) {
                if (currentValue && currentValue !== 0) {
                  logger.warn(`治疗室 ${appointment.roomId} 使用缓存数据: ${bandName} 缓存值为0，保持当前值: ${currentValue}`);
                  return currentValue;
                }
              }
              return cachedValue;
            };
            
            const oldDelta = appointment.delta;
            const oldTheta = appointment.theta;
            const oldAlpha = appointment.alpha;
            const oldBeta = appointment.beta;
            const oldGamma = appointment.gamma;
            
            appointment.delta = safeCachedValue(cachedData.delta, oldDelta, 'delta');
            appointment.theta = safeCachedValue(cachedData.theta, oldTheta, 'theta');
            appointment.alpha = safeCachedValue(cachedData.alpha, oldAlpha, 'alpha');
            appointment.beta = safeCachedValue(cachedData.beta, oldBeta, 'beta');
            appointment.gamma = safeCachedValue(cachedData.gamma, oldGamma, 'gamma');
            
            return;
          } else {
            logger.warn(`治疗室 ${appointment.roomId} 缓存数据无效且治疗未结束，保留当前值:`, {
              缓存数据: cachedData,
              治疗状态: treatmentStatus
            });
          }
        }
        
        // 没有缓存时，保留当前显示的值，不强制置为0
        // 防御性检查：如果当前值全部为0且治疗未结束，尝试从历史数据中恢复
        const currentAllZero = (!appointment.delta || appointment.delta === 0) &&
                               (!appointment.theta || appointment.theta === 0) &&
                               (!appointment.alpha || appointment.alpha === 0) &&
                               (!appointment.beta || appointment.beta === 0) &&
                               (!appointment.gamma || appointment.gamma === 0);
        
        if (currentAllZero && !isTreatmentEnded) {
          // 尝试从历史数据中恢复值
          const waveData = appointment.waveData;
          if (waveData) {
            // 尝试从最近的波线数据中恢复值
            const recoverFromHistory = (bandName) => {
              const bandData = waveData[bandName];
              if (bandData && Array.isArray(bandData) && bandData.length > 0) {
                // 取最近几个非0值的平均值
                const recentValues = bandData.slice(-10).filter(point => {
                  const value = typeof point === 'object' ? point.value : point;
                  return value && value > 0;
                });
                if (recentValues.length > 0) {
                  const avgValue = Math.round(
                    recentValues.reduce((sum, point) => {
                      const value = typeof point === 'object' ? point.value : point;
                      return sum + value;
                    }, 0) / recentValues.length
                  );
                  if (avgValue > 0) {
                    return avgValue;
                  }
                }
              }
              return null;
            };
            
            let recovered = false;
            const recoveredDelta = recoverFromHistory('delta');
            const recoveredTheta = recoverFromHistory('theta');
            const recoveredAlpha = recoverFromHistory('alpha');
            const recoveredBeta = recoverFromHistory('beta');
            const recoveredGamma = recoverFromHistory('gamma');
            
            if (recoveredDelta || recoveredTheta || recoveredAlpha || recoveredBeta || recoveredGamma) {
              if (recoveredDelta) appointment.delta = recoveredDelta;
              if (recoveredTheta) appointment.theta = recoveredTheta;
              if (recoveredAlpha) appointment.alpha = recoveredAlpha;
              if (recoveredBeta) appointment.beta = recoveredBeta;
              if (recoveredGamma) appointment.gamma = recoveredGamma;
              
              // 更新缓存
              appointment.lastValidBrainwaveData = {
                delta: appointment.delta,
                theta: appointment.theta,
                alpha: appointment.alpha,
                beta: appointment.beta,
                gamma: appointment.gamma,
                timestamp: Date.now()
              };
              
              recovered = true;
            }
          }
        }
      },

      /**
       * 更新实时数据到预约数据中
       * 注意：这个方法非常复杂，依赖很多其他方法，需要确保所有依赖的方法都可用
       */
      updateRealtimeData(realtimeData) {
        if (!realtimeData || !this.appointmentData) {
          logger.warn('updateRealtimeData: 实时数据或预约数据为空');
          return;
        }

        // 遍历所有预约数据（包括不在当前页面的），更新对应的实时状态
        this.appointmentData.forEach(appointment => {
          // 检查预约数据是否包含治疗室ID
          if (!appointment || !appointment.roomId) {
            return;
          }

          // 如果治疗已结束，不更新实时数据，保持固定值
          if (this.getTreatmentStatus(appointment) === '已结束') {
            return;
          }

          // 初始化数据缓存和接收时间
          if (!appointment.lastValidBrainwaveData) {
            appointment.lastValidBrainwaveData = null;
          }
          if (!appointment.lastReceiveTime) {
            appointment.lastReceiveTime = null;
          }

          // 只使用治疗室ID进行匹配
          const roomId = appointment.roomId;
          
          // 直接使用治疗室ID查找对应的实时数据
          const roomRealtimeData = realtimeData[roomId];
          
          // 检查5秒超时机制：如果超过5秒未收到新数据，继续使用缓存
          const now = Date.now();
          const fiveSecondsAgo = now - 5000;
          if (appointment.lastReceiveTime && appointment.lastReceiveTime < fiveSecondsAgo) {
            // 超过5秒未收到新数据，使用缓存数据
            this.useCachedBrainwaveData(appointment);
          }
          
          // 调试日志：记录数据接收情况
          if (!roomRealtimeData || !Array.isArray(roomRealtimeData) || roomRealtimeData.length === 0) {
            // 如果未超过5秒，保持当前值不变（等待下次更新）
            if (!appointment.lastReceiveTime || appointment.lastReceiveTime >= fiveSecondsAgo) {
              // 保持当前值，不做任何操作
              return;
            }
            // 超过5秒，使用缓存数据
            this.useCachedBrainwaveData(appointment);
            return;
          }

          if (roomRealtimeData && Array.isArray(roomRealtimeData) && roomRealtimeData.length > 0) {
            // 遍历数组，找到第一个有效的非0数据（优先）
            let latestData = null;
            let validDataFound = false;
            
            // 从后往前遍历，优先使用最新的有效数据
            for (let i = roomRealtimeData.length - 1; i >= 0; i--) {
              const dataItem = roomRealtimeData[i];
              if (dataItem && dataItem.frequency_bands) {
                const bands = dataItem.frequency_bands;
                // 检查是否有有效的非0值
                const hasValidNonZeroValue = 
                  (typeof bands.delta === 'number' && bands.delta > 0) ||
                  (typeof bands.theta === 'number' && bands.theta > 0) ||
                  (typeof bands.alpha === 'number' && bands.alpha > 0) ||
                  (typeof bands.beta === 'number' && bands.beta > 0) ||
                  (typeof bands.gamma === 'number' && bands.gamma > 0);
                
                // 验证数据的完整性和有效性
                const isValidData = bands &&
                  typeof bands.delta === 'number' &&
                  typeof bands.theta === 'number' &&
                  typeof bands.alpha === 'number' &&
                  typeof bands.beta === 'number' &&
                  typeof bands.gamma === 'number' &&
                  !isNaN(bands.delta) && !isNaN(bands.theta) && !isNaN(bands.alpha) && !isNaN(bands.beta) && !isNaN(bands.gamma) &&
                  isFinite(bands.delta) && isFinite(bands.theta) && isFinite(bands.alpha) && isFinite(bands.beta) && isFinite(bands.gamma) &&
                  bands.delta >= 0 && bands.theta >= 0 && bands.alpha >= 0 && bands.beta >= 0 && bands.gamma >= 0;
                
                if (isValidData && hasValidNonZeroValue) {
                  latestData = dataItem;
                  validDataFound = true;
                  break; // 找到第一个有效的非0数据，停止遍历
                } else if (isValidData && !latestData) {
                  // 如果还没有找到有效数据，记录这个有效但全为0的数据作为备选
                  latestData = dataItem;
                }
              }
            }
            
            // 如果没有找到有效数据，尝试计算所有有效数据的平均值
            if (!validDataFound && latestData) {
              // latestData 已经是有效但全为0的数据，继续使用它
            } else if (!latestData) {
              // 数组中所有数据都无效，使用缓存数据
              this.useCachedBrainwaveData(appointment);
              return;
            }
            
            // 如果 latestData 仍然为 null（不应该发生），使用缓存数据
            if (!latestData) {
              this.useCachedBrainwaveData(appointment);
              return;
            }
            
            // 只在有有效的脑波数据时才记录日志
            const hasValidFrequencyBands = latestData.frequency_bands && 
              typeof latestData.frequency_bands.delta === 'number' &&
              typeof latestData.frequency_bands.theta === 'number' &&
              typeof latestData.frequency_bands.alpha === 'number' &&
              typeof latestData.frequency_bands.beta === 'number' &&
              typeof latestData.frequency_bands.gamma === 'number' &&
              (latestData.frequency_bands.delta > 0 || latestData.frequency_bands.theta > 0 || 
               latestData.frequency_bands.alpha > 0 || latestData.frequency_bands.beta > 0 || 
               latestData.frequency_bands.gamma > 0);
            
            if (hasValidFrequencyBands) {
              logger.debug(`治疗室 ${roomId} 接收到有效的脑波数据:`, {
                frequencyBands: latestData.frequency_bands,
                sleepState: latestData.sleep_state
              });
            }
            
            // 验证房间ID匹配
            if (!this.validateRoomData(latestData, appointment)) {
              this.useCachedBrainwaveData(appointment);
              return;
            }

            // 验证通过，确保显示脑波图波形
            if (typeof appointment.hideWaveform === 'undefined') {
              this.$set(appointment, 'hideWaveform', false);
            } else {
              appointment.hideWaveform = false;
            }
            
            // 更新睡眠状态
            if (latestData.sleep_state) {
              appointment.realtime_sleep_state = this.mapSleepState(latestData.sleep_state);
            }
            
            // 更新脑波数据（百分比）
            if (latestData.frequency_bands) {
              const bands = latestData.frequency_bands;

              // 验证脑波数据的有效性
              const isValidBands = bands &&
                typeof bands.delta === 'number' &&
                typeof bands.theta === 'number' &&
                typeof bands.alpha === 'number' &&
                typeof bands.beta === 'number' &&
                typeof bands.gamma === 'number' &&
                !isNaN(bands.delta) && !isNaN(bands.theta) && !isNaN(bands.alpha) && !isNaN(bands.beta) && !isNaN(bands.gamma) &&
                isFinite(bands.delta) && isFinite(bands.theta) && isFinite(bands.alpha) && isFinite(bands.beta) && isFinite(bands.gamma) &&
                bands.delta >= 0 && bands.theta >= 0 && bands.alpha >= 0 && bands.beta >= 0 && bands.gamma >= 0;

              if (isValidBands) {
                // 检查治疗状态：除非治疗状态为"已结束"，否则即使接收到0值也不更新为0
                const treatmentStatus = this.getTreatmentStatus(appointment);
                const isTreatmentEnded = treatmentStatus === '已结束';
                
                // 改进计算方式：使用更精确的计算，避免小值被 Math.round 变成0
                const convertToPercentage = (value, originalValue, isTreatmentEnded) => {
                  const percentage = value * 100;
                  const rounded = Math.round(percentage);
                  
                  if (originalValue === 0 || value === 0) {
                    if (!isTreatmentEnded) {
                      return 1; // 治疗未结束，至少显示1
                    }
                    return 0;
                  }
                  
                  if (rounded === 0 && percentage > 0) {
                    return 1;
                  }
                  
                  if (percentage > 0 && percentage < 0.5) {
                    return 1;
                  }
                  
                  return rounded;
                };

                const newDelta = convertToPercentage(bands.delta, bands.delta, isTreatmentEnded);
                const newTheta = convertToPercentage(bands.theta, bands.theta, isTreatmentEnded);
                const newAlpha = convertToPercentage(bands.alpha, bands.alpha, isTreatmentEnded);
                const newBeta = convertToPercentage(bands.beta, bands.beta, isTreatmentEnded);
                const newGamma = convertToPercentage(bands.gamma, bands.gamma, isTreatmentEnded);
                
                // 防御性检查：确保至少有一个值不为0
                const hasValidValue = newDelta > 0 || newTheta > 0 || newAlpha > 0 || newBeta > 0 || newGamma > 0;
                
                // 防御性检查：在赋值前检查每个值，确保不会意外赋值为0（除非治疗已结束）
                const safeAssignValue = (newValue, currentValue, bandName) => {
                  // 如果新值为0且治疗未结束，使用当前值或缓存值
                  if (newValue === 0 && !isTreatmentEnded) {
                    // 优先使用当前值
                    if (currentValue !== undefined && currentValue !== null) {
                      if (currentValue !== 0) {
                        return currentValue;
                      }
                      // 如果当前值也为0，尝试使用缓存值
                      if (appointment.lastValidBrainwaveData && appointment.lastValidBrainwaveData[bandName] !== undefined) {
                        const cachedValue = appointment.lastValidBrainwaveData[bandName];
                        if (cachedValue && cachedValue !== 0) {
                          return cachedValue;
                        }
                      }
                      return currentValue; // 保持当前值，而不是返回0
                    }
                    // 如果当前值也不存在，尝试使用缓存值
                    if (appointment.lastValidBrainwaveData && appointment.lastValidBrainwaveData[bandName] !== undefined) {
                      const cachedValue = appointment.lastValidBrainwaveData[bandName];
                      if (cachedValue && cachedValue !== 0) {
                        return cachedValue;
                      }
                    }
                    return currentValue !== undefined && currentValue !== null ? currentValue : 0;
                  }
                  return newValue;
                };
                
                if (hasValidValue) {
                  // 有有效值，正常更新（但每个值都要经过安全检查）
                  const oldDelta = appointment.delta;
                  const oldTheta = appointment.theta;
                  const oldAlpha = appointment.alpha;
                  const oldBeta = appointment.beta;
                  const oldGamma = appointment.gamma;
                  
                  appointment.delta = safeAssignValue(newDelta, oldDelta, 'delta');
                  appointment.theta = safeAssignValue(newTheta, oldTheta, 'theta');
                  appointment.alpha = safeAssignValue(newAlpha, oldAlpha, 'alpha');
                  appointment.beta = safeAssignValue(newBeta, oldBeta, 'beta');
                  appointment.gamma = safeAssignValue(newGamma, oldGamma, 'gamma');
                  
                  // 再次检查：如果赋值后所有值都为0且治疗未结束，使用缓存数据
                  const afterAssignAllZero = appointment.delta === 0 && appointment.theta === 0 && 
                                            appointment.alpha === 0 && appointment.beta === 0 && appointment.gamma === 0;
                  if (afterAssignAllZero && !isTreatmentEnded) {
                    logger.error(`治疗室 ${appointment.roomId} 赋值后所有值都为0，但治疗未结束，使用缓存数据`);
                    this.useCachedBrainwaveData(appointment);
                  } else {
                    // 缓存最后一次有效的脑波数据
                    appointment.lastValidBrainwaveData = {
                      delta: appointment.delta,
                      theta: appointment.theta,
                      alpha: appointment.alpha,
                      beta: appointment.beta,
                      gamma: appointment.gamma,
                      timestamp: Date.now()
                    };
                    // 记录成功接收数据的时间
                    appointment.lastReceiveTime = Date.now();
                  }
                } else if (isTreatmentEnded) {
                  // 治疗已结束，允许更新为0
                  appointment.delta = newDelta;
                  appointment.theta = newTheta;
                  appointment.alpha = newAlpha;
                  appointment.beta = newBeta;
                  appointment.gamma = newGamma;
                } else {
                  // 所有值都为0，但治疗未结束，使用缓存数据
                  this.useCachedBrainwaveData(appointment);
                }
              } else {
                // 数据无效时使用缓存数据
                this.useCachedBrainwaveData(appointment);
              }
            }
            
            // 更新脑波图数据：将所有数组元素都添加到waveData中
            if (roomRealtimeData && roomRealtimeData.length > 0) {
              // 遍历数组，将所有有效数据点添加到waveData中
              roomRealtimeData.forEach((dataItem) => {
                if (dataItem && dataItem.frequency_bands) {
                  const bands = dataItem.frequency_bands;
                  // 验证数据的有效性
                  const isValidBands = bands &&
                    typeof bands.delta === 'number' &&
                    typeof bands.theta === 'number' &&
                    typeof bands.alpha === 'number' &&
                    typeof bands.beta === 'number' &&
                    typeof bands.gamma === 'number' &&
                    !isNaN(bands.delta) && !isNaN(bands.theta) && !isNaN(bands.alpha) && !isNaN(bands.beta) && !isNaN(bands.gamma) &&
                    isFinite(bands.delta) && isFinite(bands.theta) && isFinite(bands.alpha) && isFinite(bands.beta) && isFinite(bands.gamma) &&
                    bands.delta >= 0 && bands.theta >= 0 && bands.alpha >= 0 && bands.beta >= 0 && bands.gamma >= 0;
                  
                  if (isValidBands) {
                    // 将每个数据点添加到waveData中
                    this.updateWaveData(appointment, bands);
                  }
                }
              });
              
              // 保存数组数据到appointment中，用于每秒切换显示；限制长度防止内存无限增长
              if (roomRealtimeData.length > 0) {
                const maxRealtimeArrayLength = 100;
                const capped = roomRealtimeData.length > maxRealtimeArrayLength
                  ? roomRealtimeData.slice(-maxRealtimeArrayLength)
                  : roomRealtimeData;
                // 深拷贝数组，避免引用问题导致的数据丢失
                appointment.realtimeDataArray = capped.map(item => {
                  if (item && item.frequency_bands) {
                    return {
                      ...item,
                      frequency_bands: { ...item.frequency_bands }
                    };
                  }
                  return { ...item };
                });
                
                // 初始化显示索引（如果还没有）
                if (appointment.currentDisplayIndex === undefined) {
                  appointment.currentDisplayIndex = appointment.realtimeDataArray.length - 1; // 默认显示最新的
                }
                
                // 立即更新显示的脑波值（使用最新数据或当前索引对应的数据）
                const displayData = latestData || appointment.realtimeDataArray[appointment.currentDisplayIndex];
                if (displayData && displayData.frequency_bands) {
                  const displayBands = displayData.frequency_bands;
                  const treatmentStatus = this.getTreatmentStatus(appointment);
                  const isTreatmentEnded = treatmentStatus === '已结束';
                  
                  // 转换并更新显示的脑波值
                  const convertToPercentage = (value) => {
                    const percentage = value * 100;
                    const rounded = Math.round(percentage);
                    if (value === 0 && !isTreatmentEnded) return 1;
                    if (rounded === 0 && percentage > 0) return 1;
                    if (percentage > 0 && percentage < 0.5) return 1;
                    return rounded;
                  };
                  
                  appointment.delta = convertToPercentage(displayBands.delta);
                  appointment.theta = convertToPercentage(displayBands.theta);
                  appointment.alpha = convertToPercentage(displayBands.alpha);
                  appointment.beta = convertToPercentage(displayBands.beta);
                  appointment.gamma = convertToPercentage(displayBands.gamma);
                  
                  // 更新缓存和接收时间
                  appointment.lastValidBrainwaveData = {
                    delta: appointment.delta,
                    theta: appointment.theta,
                    alpha: appointment.alpha,
                    beta: appointment.beta,
                    gamma: appointment.gamma,
                    timestamp: Date.now()
                  };
                  appointment.lastReceiveTime = Date.now();
                }
              }
            }
            
            // 更新质量分数（只有在成功找到有效数据时才更新）
            if (latestData && latestData.quality_score !== undefined) {
              appointment.quality_score = latestData.quality_score;
            }
          } else {
            // 无法接收到脑波仪接口数据时的处理
            // 确保脑波图波形始终显示，不隐藏
            if (typeof appointment.hideWaveform === 'undefined') {
              this.$set(appointment, 'hideWaveform', false);
            } else {
              appointment.hideWaveform = false;
            }

            // 使用缓存数据而不是清空数据
            this.useCachedBrainwaveData(appointment);
          }
          
          // 最终检查：确保值不会意外变成0（除非治疗已结束）
          const treatmentStatus = this.getTreatmentStatus(appointment);
          const isTreatmentEnded = treatmentStatus === '已结束';
          
          if (!isTreatmentEnded) {
            const finalAllZero = (!appointment.delta || appointment.delta === 0) &&
                                (!appointment.theta || appointment.theta === 0) &&
                                (!appointment.alpha || appointment.alpha === 0) &&
                                (!appointment.beta || appointment.beta === 0) &&
                                (!appointment.gamma || appointment.gamma === 0);
            
            if (finalAllZero) {
              // 如果所有值都为0，尝试从历史数据恢复
              const waveData = appointment.waveData;
              if (waveData) {
                const recoverFromHistory = (bandName) => {
                  const bandData = waveData[bandName];
                  if (bandData && Array.isArray(bandData) && bandData.length > 0) {
                    const recentValues = bandData.slice(-10).filter(point => {
                      const value = typeof point === 'object' ? point.value : point;
                      return value && value > 0;
                    });
                    if (recentValues.length > 0) {
                      const avgValue = Math.round(
                        recentValues.reduce((sum, point) => {
                          const value = typeof point === 'object' ? point.value : point;
                          return sum + value;
                        }, 0) / recentValues.length
                      );
                      if (avgValue > 0) {
                        return avgValue;
                      }
                    }
                  }
                  return null;
                };
                
                let recovered = false;
                if (!appointment.delta || appointment.delta === 0) {
                  const recoveredDelta = recoverFromHistory('delta');
                  if (recoveredDelta) {
                    appointment.delta = recoveredDelta;
                    recovered = true;
                  }
                }
                if (!appointment.theta || appointment.theta === 0) {
                  const recoveredTheta = recoverFromHistory('theta');
                  if (recoveredTheta) {
                    appointment.theta = recoveredTheta;
                    recovered = true;
                  }
                }
                if (!appointment.alpha || appointment.alpha === 0) {
                  const recoveredAlpha = recoverFromHistory('alpha');
                  if (recoveredAlpha) {
                    appointment.alpha = recoveredAlpha;
                    recovered = true;
                  }
                }
                if (!appointment.beta || appointment.beta === 0) {
                  const recoveredBeta = recoverFromHistory('beta');
                  if (recoveredBeta) {
                    appointment.beta = recoveredBeta;
                    recovered = true;
                  }
                }
                if (!appointment.gamma || appointment.gamma === 0) {
                  const recoveredGamma = recoverFromHistory('gamma');
                  if (recoveredGamma) {
                    appointment.gamma = recoveredGamma;
                    recovered = true;
                  }
                }
                
                if (recovered) {
                  // 更新缓存
                  appointment.lastValidBrainwaveData = {
                    delta: appointment.delta,
                    theta: appointment.theta,
                    alpha: appointment.alpha,
                    beta: appointment.beta,
                    gamma: appointment.gamma,
                    timestamp: Date.now()
                  };
                }
              }
            }
          }
        });
      }
    }
  };
}

